import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MdArrowBack, MdHome, MdLockOutline } from "react-icons/md";
import { elakayAutoText } from "../pageInside/dataElakay";
import { RETURN_URL_QUERY_PARAM } from "../../utils/userAddingRoleToken";
import { POST_AUTH_DESTINATION_KEY, safePostAuthDestination } from "./postAuthRedirect";
import { previousAppPage } from "./navigationHistory";
import "./AuthPageNav.css";

// The page a guard sent the visitor away from (?returnUrl= first, else the
// copy remembered in this tab). Read only: postAuthRedirect consumes it.
export const pendingAuthDestination = (search) => {
  try {
    const fromQuery = safePostAuthDestination(new URLSearchParams(search).get(RETURN_URL_QUERY_PARAM));
    if (fromQuery) return fromQuery;
    const stored = JSON.parse(window.sessionStorage.getItem(POST_AUTH_DESTINATION_KEY) || "null");
    return safePostAuthDestination(stored?.destination);
  } catch (_) {
    return null;
  }
};

export const useAuthPageNavigation = () => {
  const location = useLocation();
  return useMemo(() => {
    const destination = pendingAuthDestination(location.search);
    // "Back" never re-opens the protected destination (it would only send
    // the visitor to this page again) and never another auth page.
    const back = previousAppPage({ current: `${location.pathname}${location.search}`, exclude: destination ? [destination] : [] });
    return { destination, back: back && back !== "/" ? back : null };
  }, [location.pathname, location.search]);
};

// Home + Back (when a valid previous Elakay page exists) for the auth pages,
// which have their own header instead of the Elakay one.
const AuthPageNav = ({ className = "" }) => {
  const navigate = useNavigate();
  const { back } = useAuthPageNavigation();
  return (
    <nav className={`auth-page-nav ${className}`} aria-label={elakayAutoText("h_authnav_label", "Leave this page")}>
      {back && (
        <button type="button" className="auth-page-nav__link" onClick={() => navigate(back)}>
          <MdArrowBack aria-hidden="true" />
          <span>{elakayAutoText("h_authnav_back", "Back")}</span>
        </button>
      )}
      <Link className="auth-page-nav__link auth-page-nav__link--home" to="/">
        <MdHome aria-hidden="true" />
        <span>{elakayAutoText("h_authnav_home", "Home")}</span>
      </Link>
    </nav>
  );
};

// Shown above the sign-in / sign-up form only when a protected page sent
// the visitor here: says where they will go, with a way out.
export const AuthDestinationNotice = () => {
  const navigate = useNavigate();
  const { destination, back } = useAuthPageNavigation();
  if (!destination || destination === "/") return null;
  return (
    <div className="auth-destination-notice" role="status">
      <MdLockOutline className="auth-destination-notice__icon" aria-hidden="true" />
      <div className="auth-destination-notice__body">
        <strong>{elakayAutoText("h_authnav_notice_title", "Sign in to continue")}</strong>
        <span>{elakayAutoText("h_authnav_notice_text", "After signing in you will return to the page you asked for: {page}", { page: destination })}</span>
        <div className="auth-destination-notice__actions">
          {back && (
            <button type="button" className="auth-destination-notice__action" onClick={() => navigate(back)}>
              {elakayAutoText("h_authnav_notice_back", "Return to the previous page")}
            </button>
          )}
          <Link className="auth-destination-notice__action" to="/">
            {elakayAutoText("h_authnav_notice_home", "Continue browsing on Home")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthPageNav;
